import { apiFetch, get, post, patch, del } from "./api";

export type QuestionType = "single_choice" | "multi_select" | "true_false" | "free_text";
export type QuestionVisibility = "global" | "company_specific";
export type LanguageTag = "vi" | "en" | "km";
/** FR-055: a member's organisational level. Mirrors the backend's PositionType. */
export type PositionType = "employee" | "manager" | "bod";

export type Question = {
  id: string;
  language: LanguageTag;
  text: string;
  type: QuestionType;
  options: string[] | null;
  correctAnswer: string[] | null;
  explanationHtml: string;
  explanationImageKeys: string[];
  appTags: string[];
  visibility: QuestionVisibility;
  /** FR-056: appended to every test, never scored. */
  isFeedback: boolean;
  /** FR-055: empty = everyone. */
  targetPositionTypes: PositionType[];
  status: "active" | "inactive";
  companyGrantIds: string[];
  createdAt: number;
  updatedAt: number;
};

export type QuestionInput = {
  language: LanguageTag;
  text: string;
  type: QuestionType;
  options?: string[] | null;
  correctAnswer?: string[] | null;
  explanationHtml: string;
  explanationImageKeys?: string[];
  appTags: string[];
  visibility: QuestionVisibility;
  companyGrantIds?: string[];
  /** FR-056: appended to every test regardless of tags, and never scored. */
  isFeedback?: boolean;
  /** FR-055: restricts who is shown the question. Empty = everyone. */
  targetPositionTypes?: PositionType[];
};

export type QuestionListFilter = {
  app?: string;
  systemId?: string;
  status?: "active" | "inactive";
  language?: LanguageTag;
};

export function listQuestions(filter: QuestionListFilter = {}) {
  const params = new URLSearchParams();
  if (filter.app) params.set("app", filter.app);
  if (filter.systemId) params.set("systemId", filter.systemId);
  if (filter.status) params.set("status", filter.status);
  if (filter.language) params.set("language", filter.language);
  const qs = params.toString();
  return get<Question[]>(`/co/questions${qs ? `?${qs}` : ""}`);
}

export function createQuestion(input: QuestionInput) {
  return post<Question>("/co/questions", input);
}

export function updateQuestion(id: string, input: Partial<QuestionInput>) {
  return patch<Question>(`/co/questions/${encodeURIComponent(id)}`, input);
}

export function deactivateQuestion(id: string) {
  return post<{ status: "inactive" }>(`/co/questions/${encodeURIComponent(id)}/deactivate`);
}

/** FR-073: the other half of the Question Bank's Deactivate toggle. */
export function reactivateQuestion(id: string) {
  return post<{ status: "active" }>(`/co/questions/${encodeURIComponent(id)}/reactivate`);
}

/** FR-073: hard delete. Throws ApiError 409 if the question has already gone out in an
 * assignment — the caller shows that message and the CO Member deactivates instead. */
export function removeQuestion(id: string) {
  return del<void>(`/co/questions/${encodeURIComponent(id)}`);
}

/** FR-046: one row per question. Same shape as the roster upload's result, deliberately —
 * created count plus per-row rejections, so a bad row costs that row and nothing else. */
export type QuestionUploadResult = {
  created: number;
  rejectedRows: { row: number; reason: string }[];
};

export function bulkUploadQuestions(file: File) {
  const form = new FormData();
  form.set("file", file);
  return apiFetch<QuestionUploadResult>("/co/questions/bulk-upload", { method: "POST", body: form });
}
